import { useMemo } from 'react';
import { Box, Tag, Text } from '@nimbus-ds/components';
import { OrderBulkDto } from '@tiendanube/common';
import { Status } from '@tiendanube/common/src/enums';
import ModalAside from 'App/components/lab/ModalAside/ModalAside';
import {
  CancelAndConfirmButtons,
  HeaderContent,
  HeaderTop,
} from 'commons/components';
import { useFormatCurrency } from 'domains/Billing/hooks';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';

interface OrderMarkAsPaidModalAsideProps {
  orderIds: string[];
  orders: OrderBulkDto[];
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (orderIdsToPay: string[]) => void;
}

export function OrderMarkAsPaidModalAside({
  orderIds,
  orders,
  isOpen,
  onClose,
  onConfirm,
}: Readonly<OrderMarkAsPaidModalAsideProps>): JSX.Element {
  const t = useTranslationOrders();
  const formatCurrency = useFormatCurrency();
  const { selectedOrders, showTotal } = useMemo(() => {
    const validPaymentStatus = [
      Status.AUTHORIZED,
      Status.PENDING,
      Status.PARTIALLY_PAID,
      Status.VOIDED,
    ];
    const selectedOrders: OrderBulkDto[] = [];
    let showTotal = true;
    orders.forEach((order) => {
      if (
        orderIds.includes(order.orderId) &&
        order.status === Status.OPEN &&
        validPaymentStatus.includes(order.paymentStatus as Status)
      ) {
        selectedOrders.push(order);
        if (order.paymentStatus === Status.PARTIALLY_PAID) {
          showTotal = false;
        }
      }
    });
    selectedOrders.sort((a, b) => b.number - a.number);
    return { selectedOrders, showTotal };
  }, [orderIds, orders]);

  const total = useMemo(
    () => selectedOrders.reduce((acc, order) => acc + order.total, 0),
    [selectedOrders],
  );

  const thereAreOrdersToPay = selectedOrders.length > 0;

  const handleConfirm = () => {
    onConfirm(selectedOrders.map((order) => order.orderId));
  };

  return (
    <ModalAside
      isOpen={isOpen}
      headerTop={<HeaderTop navigation={{ onClick: () => onClose() }} />}
      headerContent={
        <HeaderContent title={t('orderMarkAsPaidModalAside.title')} />
      }
    >
      <Box paddingY="4" display="flex" flexDirection="column">
        <Box display="flex" flexDirection="column" marginBottom="8">
          {thereAreOrdersToPay ? (
            <Text>
              {showTotal
                ? t('orderMarkAsPaidModalAside.body', {
                    total: formatCurrency(total),
                  })
                : t('orderMarkAsPaidModalAside.bodyWithoutTotal')}
            </Text>
          ) : (
            <Text>{t('orderMarkAsPaidModalAside.noOrdersBody')}</Text>
          )}
          {thereAreOrdersToPay && (
            <Box
              display="flex"
              flexDirection="row"
              flexWrap="wrap"
              marginTop="6"
            >
              {selectedOrders.map((order) => (
                <Box key={order.orderId} marginRight="2" marginBottom="2">
                  <Tag>#{order.number}</Tag>
                </Box>
              ))}
            </Box>
          )}
        </Box>
        <CancelAndConfirmButtons
          onCancel={onClose}
          onConfirm={handleConfirm}
          cancelText={t('orderMarkAsPaidModalAside.cancel')}
          confirmText={t('orderMarkAsPaidModalAside.confirm')}
          isConfirmDisabled={!thereAreOrdersToPay}
        />
      </Box>
    </ModalAside>
  );
}
